import { type FC, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Box, Typography } from '@mui/material';

// common
import DynamicOperationCodeField from 'common/components/OperationCodeField/DynamicOperationCodeField';
import SchemaForm from 'common/SchemaForm';
import MuiLoading from 'common/components/MuiLoading';
import { formatDateWithoutTimezone } from 'common/utils';

// screen
import DialogWrapper from 'modules/suppliers/detail/components/modals/DialogWrapper';
import PdfViewer from 'modules/suppliers/detail/components/modals/PdfViewer';
import { OtherDocument } from 'modules/suppliers/services/useOtherDocuments';
import { useTabsData } from 'modules/suppliers/detail/context/useTabsData';
import useOtherDocumentsDetails, {
  type OtherDocumentDetails,
} from 'modules/suppliers/services/useOtherDocumentsDetails';
import PdfList from 'modules/suppliers/detail/components/modals/details/PdfList';

interface OtherDocumentDetailsProps {
  open: boolean;
  onClose: () => void;
  item: OtherDocument;
}

const OtherDocumentDetails: FC<OtherDocumentDetailsProps> = (props) => {
  const { open, onClose, item } = props;

  const [pdfModalOpen, setPdfModalOpen] = useState<boolean>(false);
  const [selectedPdf, setSelectedPdf] = useState<string | null>(null);
  const [selectedPdfFileName, setSelectedPdfFileName] = useState<string>('');

  const { formatMessage } = useIntl();

  const { params } = useTabsData();

  const { data } = useOtherDocumentsDetails({
    supplierProfileOperationCode: params.supplierOperationCode,
    customerOperationCode: params.customerOperationCode,
    documentOperationCode: item.operationCode,
  });

  if (!data) return <MuiLoading />;

  return (
    <DialogWrapper
      title={
        <>
          <FormattedMessage id="supplier.otherDocument.detailsModalTitle" />: {data.number}
        </>
      }
      open={open}
      onClose={onClose}
    >
      <SchemaForm<OtherDocumentDetails>
        formHookProps={{
          values: {
            ...data,
            expiredAt: data.isUnlimited
              ? formatMessage({ id: 'noExpiration' })
              : data.expiredAt
                ? formatDateWithoutTimezone(data.expiredAt)
                : '',
            customSwitch: data.expirationDate ? false : true,
            hasPdf: !!data.pdfUrl,
          },
        }}
        globalProps={{ readOnly: true }}
        schema={{
          fields: ({
            formProps: {
              intl: { formatMessage },
            },
          }) => {
            return {
              documentNumber: {
                mode: 'text-field',
                propsWrapper: { desktop: 6 },
                label: formatMessage({ id: 'supplier.otherDocument.number' }),
                placeholder: formatMessage({
                  id: 'supplier.otherDocument.number',
                }),
                rules: { required: true },
              },
              operationCode: {
                mode: 'raw',
                propsWrapper: { desktop: 6 },
                render: (params) => (
                  <DynamicOperationCodeField
                    {...params}
                    label={formatMessage({
                      id: 'supplier.otherDocument.operationCode',
                    })}
                  />
                ),
              },
              name: {
                mode: 'text-field',
                propsWrapper: { desktop: 6 },
                label: formatMessage({ id: 'supplier.otherDocument.name' }),
                placeholder: formatMessage({
                  id: 'supplier.otherDocument.name',
                }),
                rules: { required: true },
              },
              type: {
                mode: 'text-field',
                propsWrapper: { desktop: 6 },
                label: formatMessage({
                  id: 'supplier.otherDocument.documentClassification',
                }),
                placeholder: formatMessage({
                  id: 'supplier.otherDocument.documentClassification',
                }),
                rules: { required: true },
              },
              expiredAt: {
                mode: 'text-field',
                propsWrapper: { desktop: 6 },
                label: formatMessage({
                  id: 'supplier.businessRegistration.expirationDate',
                }),
                placeholder: formatMessage({
                  id: 'supplier.businessRegistration.expirationDate',
                }),
                rules: { required: true },
              },
              supplierName: {
                mode: 'text-field',
                propsWrapper: { desktop: 6 },
                label: formatMessage({
                  id: 'supplier.otherDocument.secondarySupplierName',
                }),
                placeholder: formatMessage({
                  id: 'supplier.otherDocument.secondarySupplierName',
                }),
              },
              isUnlimited: {
                mode: 'switch',
                propsWrapper: { desktop: 6 },
                label: formatMessage({ id: 'noExpiration' }),
                labelPlacement: 'top',
              },
              hasPdf: {
                mode: 'raw',
                render: () => {
                  const pdfPaths = (data.pdfPath as string[]) || [];

                  return !pdfPaths.length ? null : (
                    <Box>
                      <Typography sx={{ marginBottom: 1, fontWeight: 500 }}>
                        PDF Documents:
                      </Typography>
                      <Typography>
                        <PdfList
                          pdfPaths={pdfPaths}
                          onPdfClick={(pdfUrl, fileName) => {
                            setSelectedPdf(pdfUrl);
                            setSelectedPdfFileName(fileName);
                            setPdfModalOpen(true);
                          }}
                        />
                      </Typography>
                    </Box>
                  );
                },
              },
            };
          },
        }}
      />
      <PdfViewer
        open={pdfModalOpen}
        onClose={() => setPdfModalOpen(false)}
        pdfUrl={selectedPdf}
        title={selectedPdfFileName || 'PDF Document'}
      />
    </DialogWrapper>
  );
};

export default OtherDocumentDetails;
